import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { Form } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridRow, StyledGridCol } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import FieldVerificationContext, {
  useFieldVerificationState,
} from "@/contexts/FieldVerificationContext";
import SecureFormItem from "@/common/SecureFormItem";
import VerificationHint from "@/common/VerificationHint";
import { getOptionsFromRefList } from "@/util/common_function";
import PersonalBasicInformation from "../PersonalInformationDetails/PersonalBasicInformation";
import AliasesList from "../PersonalInformationDetails/AliasesList";
import AddressesList from "../PersonalInformationDetails/AddressesList";
import { BookingsSectionTitle } from "../styles";
import { useSelector } from "react-redux";
import { SCHEMA_NAME } from "@/util/common_variable";

const InmateAssociatesForm = forwardRef(
  ({ selectedAssociate, onFormChange, startingPoint }, associateFormRef) => {
    const [form] = Form.useForm();
    const fullSearch             = useSelector((state) => state.fullSearch);
    const { bookingsFieldsEnabled } = useSelector((state) => state.bookings);
    const verification = useFieldVerificationState("BookingsJacketsJackets");

    const [selectedAliases, setSelectedAliases]   = useState(
      selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAliases || []
    );
    const [selectedAddresses, setSelectedAddresses] = useState(
      selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
    );

    // -----------------------------------------------------------------------
    // Sync selectedAssociate → form
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (selectedAssociate) {
        form.setFieldsValue({
          associateTypeID: selectedAssociate?.associateTypeID,
          relationship:    selectedAssociate?.relationship,
          comments:        selectedAssociate?.comments,
        });
        setSelectedAliases(selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAliases  || []);
        setSelectedAddresses(selectedAssociate?.jackets?.[0]?.bio?.[0]?.bioAddresses || []);
        // Name/phone fields get marked unverified by PersonalBasicInformation
        // itself, which receives selectedAssociate.jackets[0].bio[0] below.
      } else {
        form.resetFields();
        setSelectedAliases([]);
        setSelectedAddresses([]);
      }
    }, [selectedAssociate, form]);

    useEffect(() => {
      form.setFieldsValue({ bioAliases: selectedAliases });
    }, [selectedAliases, form]);

    useEffect(() => {
      form.setFieldsValue({ bioAddresses: selectedAddresses });
    }, [selectedAddresses, form]);

    useImperativeHandle(associateFormRef, () => ({
      form,
      hasUnverifiedFields: verification.hasUnverifiedFields,
      getUnverifiedFieldNames: verification.getUnverifiedFieldNames,
    }));

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <FieldVerificationContext.Provider value={verification}>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.ASSOCIATES}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form
          form={form}
          onValuesChange={(_, allValues) => {
            if (onFormChange) onFormChange(allValues);
          }}
        >
          <StyledGridRow gutter={16}>

            {/* Associate Type */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="associateTypeID"
                formItemProps={{
                  name: "associateTypeID", label: "Associate Type",
                  required: !fullSearch,
                  rules: !fullSearch
                    ? [{ required: true, message: "Associate Type is required" }]
                    : [],
                }}
              >
                <Select
                  options={getOptionsFromRefList("Inmate_Associate_Type", false, false, true)}
                  placeholder="Associate Type"
                  showSearch
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* Relationship */}
            <StyledGridCol span={8}>
              <SecureFormItem
                fieldName="relationship"
                formItemProps={{
                  name: "relationship", label: "Relationship",
                  required: !fullSearch,
                  rules: !fullSearch
                    ? [{ required: true, message: "Relationship is required" }]
                    : [],
                }}
              >
                <Select
                  options={getOptionsFromRefList("Relationship")}
                  placeholder="Relationship"
                  showSearch
                  variant="outlined"
                />
              </SecureFormItem>
            </StyledGridCol>

          </StyledGridRow>

          {/* Personal Information — child components inherit the context */}
          <BookingsSectionTitle>
            <span>Personal Information</span>
          </BookingsSectionTitle>
          <VerificationHint />
          <PersonalBasicInformation
            selectedAssociate={selectedAssociate?.jackets?.[0]?.bio?.[0]}
            fromJacketSearch={!!selectedAssociate?.isFreshJacketPick}
            form={form}
            associates
            startingPoint={startingPoint}
          />

          <AliasesList
            showQuickAddForm={false}
            selectedAliases={selectedAliases}
            associates
            onAliasesChange={setSelectedAliases}
            startingPoint={startingPoint}
          />

          {/* Hidden field — no security needed */}
          <FormItem name="bioAliases" hidden>
            <TextField />
          </FormItem>

          <AddressesList
            selectedAddresses={selectedAddresses}
            associates
            onAddressesChange={setSelectedAddresses}
            startingPoint={startingPoint}
          />

          {/* Hidden field — no security needed */}
          <FormItem name="bioAddresses" hidden>
            <TextField />
          </FormItem>

          {/* Comments */}
          <BookingsSectionTitle>
            <span>Comments</span>
          </BookingsSectionTitle>
          <SecureFormItem
            fieldName="comments"
            formItemProps={{ name: "comments" }}
          >
            <TextField inputType="textarea" />
          </SecureFormItem>

        </Form>
      </FieldSecurityProvider>
      </FieldVerificationContext.Provider>
    );
  }
);

export default InmateAssociatesForm;